import React, { useState, useEffect } from 'react';
import { TrendingUp, Building2, Users, Monitor, DollarSign, Activity, Globe, Award, Package, Image, Layers, AlertCircle, CheckCircle, XCircle } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area } from 'recharts';

const SuperAdminAnalytics = () => {
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
  
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('7');
  const [globalStats, setGlobalStats] = useState({
    organizations: { total: 0 },
    revenue: { mrr: 0, activeOrgs: 0, trialOrgs: 0 },
    screens: { total: 0, online: 0, offline: 0 },
    users: { total: 0 },
    media: { total: 0 },
    playlists: { total: 0 },
    widgets: { total: 0 }
  });
  const [allOrganizations, setAllOrganizations] = useState([]);
  const [globalActivity, setGlobalActivity] = useState([]);
  const [orgComparison, setOrgComparison] = useState([]);

  useEffect(() => {
    loadGlobalAnalytics();
  }, [timeRange]);

  const loadGlobalAnalytics = async () => {
  setLoading(true);
  try {
    const token = localStorage.getItem('token');
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };

    const [stats, orgs, activity, comparison] = await Promise.all([
      fetch(`${API_URL}/analytics/global/overview`, { headers }).then(r => r.json()),
      fetch(`${API_URL}/analytics/global/organizations`, { headers }).then(r => r.json()),
      fetch(`${API_URL}/analytics/global/activity?days=${timeRange}&limit=20`, { headers }).then(r => r.json()),
      fetch(`${API_URL}/analytics/global/comparison`, { headers }).then(r => r.json())
    ]);

      setGlobalStats(stats);
      setAllOrganizations(orgs);
      setGlobalActivity(activity);
      setOrgComparison(comparison);
    } catch (error) {
      console.error('Error loading global analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Gerade eben';
    if (diffMins < 60) return `vor ${diffMins} Min`;
    if (diffHours < 24) return `vor ${diffHours} Std`;
    if (diffDays < 7) return `vor ${diffDays} Tag${diffDays > 1 ? 'en' : ''}`;
    
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const getTierBadgeColor = (tier) => {
    switch (tier) {
      case 'enterprise': return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400';
      case 'business': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case 'starter': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'trialing': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'cancelled': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const prepareOrgComparisonChart = () => {
    return orgComparison.slice(0, 10).map(org => ({
      name: org.organization_name.substring(0, 15) + '...',
      mrr: parseInt(org.mrr),
      screens: parseInt(org.screen_count)
    }));
  };

  // Prepare Tier Distribution Data
  const prepareTierDistribution = () => {
    const tierCounts = allOrganizations.reduce((acc, org) => {
      const tier = org.tier || 'none';
      acc[tier] = (acc[tier] || 0) + 1;
      return acc;
    }, {});

    return Object.entries(tierCounts).map(([tier, count]) => ({
      name: tier.charAt(0).toUpperCase() + tier.slice(1),
      value: count
    }));
  };

  // Prepare Status Distribution Data
  const prepareStatusDistribution = () => {
    const statusCounts = allOrganizations.reduce((acc, org) => {
      const status = org.subscriptionStatus || 'none';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});

    return Object.entries(statusCounts).map(([status, count]) => ({
      name: status.charAt(0).toUpperCase() + status.slice(1),
      value: count
    }));
  };

  // Calculate Platform Stats
  const calculatePlatformStats = () => {
    const totalScreens = allOrganizations.reduce((sum, org) => sum + org.screenCount, 0);
    const totalMedia = allOrganizations.reduce((sum, org) => sum + org.mediaCount, 0);
    const totalUsers = allOrganizations.reduce((sum, org) => sum + org.userCount, 0);
    const avgScreensPerOrg = allOrganizations.length > 0 ? (totalScreens / allOrganizations.length).toFixed(1) : 0;
    const avgUsersPerOrg = allOrganizations.length > 0 ? (totalUsers / allOrganizations.length).toFixed(1) : 0;

    return {
      totalScreens,
      totalMedia,
      totalUsers,
      avgScreensPerOrg,
      avgUsersPerOrg
    };
  };

  // Get top performing orgs
  const getTopPerformingOrgs = () => {
    return [...allOrganizations]
      .sort((a, b) => b.screenCount - a.screenCount)
      .slice(0, 5);
  };

  const platformStats = calculatePlatformStats();
  const topOrgs = getTopPerformingOrgs();

  const COLORS = ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#ec4899'];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
                <Globe className="w-8 h-8 text-purple-500" />
                Global Analytics Dashboard
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-2">
                Übersicht über alle Organizations und Plattform-Performance
              </p>
            </div>

            {/* Time Range Selector */}
            <div className="flex items-center gap-2 bg-white dark:bg-gray-800 rounded-lg p-1 shadow-sm">
              <button
                onClick={() => setTimeRange('7')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  timeRange === '7'
                    ? 'bg-purple-500 text-white'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                7 Tage
              </button>
              <button
                onClick={() => setTimeRange('30')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  timeRange === '30'
                    ? 'bg-purple-500 text-white'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                30 Tage
              </button>
              <button
                onClick={() => setTimeRange('90')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  timeRange === '90'
                    ? 'bg-purple-500 text-white'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                90 Tage
              </button>
            </div>
          </div>
        </div>

        {/* Global Stats Cards - Row 1 */}
        {globalStats && globalStats.organizations && globalStats.revenue && globalStats.screens && globalStats.users && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {/* Total Organizations */}
            <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-sm p-6 text-white">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-white/20 rounded-lg">
                  <Building2 className="w-6 h-6" />
                </div>
              </div>
              <h3 className="text-3xl font-bold mb-1">
                {globalStats.organizations.total}
              </h3>
              <p className="text-purple-100 text-sm">Total Organizations</p>
            </div>

            {/* Global MRR */}
            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-sm p-6 text-white">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-white/20 rounded-lg">
                  <DollarSign className="w-6 h-6" />
                </div>
              </div>
              <h3 className="text-3xl font-bold mb-1">
                CHF {formatNumber(globalStats.revenue.mrr)}
              </h3>
              <p className="text-green-100 text-sm">Monthly Recurring Revenue</p>
              <p className="text-green-200 text-xs mt-1">
                {globalStats.revenue.activeOrgs} active • {globalStats.revenue.trialOrgs} trial
              </p>
            </div>

            {/* Total Screens */}
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-sm p-6 text-white">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-white/20 rounded-lg">
                  <Monitor className="w-6 h-6" />
                </div>
                <span className="text-sm font-medium">
                  {globalStats.screens.online}/{globalStats.screens.total} Online
                </span>
              </div>
              <h3 className="text-3xl font-bold mb-1">
                {globalStats.screens.total}
              </h3>
              <p className="text-blue-100 text-sm">Total Screens</p>
            </div>

            {/* Total Users */}
            <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl shadow-sm p-6 text-white">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-white/20 rounded-lg">
                  <Users className="w-6 h-6" />
                </div>
              </div>
              <h3 className="text-3xl font-bold mb-1">
                {globalStats.users.total}
              </h3>
              <p className="text-orange-100 text-sm">Total Users</p>
            </div>
          </div>
        )}

        {/* Platform Stats Cards - Row 2 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Total Media */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-pink-100 dark:bg-pink-900/30 rounded-lg">
                <Image className="w-6 h-6 text-pink-600 dark:text-pink-400" />
              </div>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
              {formatNumber(globalStats.media.total)}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">Total Media Items</p>
          </div>

          {/* Total Playlists */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                <Layers className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
              </div>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
              {globalStats.playlists.total}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">Total Playlists</p>
          </div>

          {/* Total Widgets */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-cyan-100 dark:bg-cyan-900/30 rounded-lg">
                <Package className="w-6 h-6 text-cyan-600 dark:text-cyan-400" />
              </div>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
              {globalStats.widgets.total}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">Active Widgets</p>
          </div>

          {/* Avg Screens per Org */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-teal-100 dark:bg-teal-900/30 rounded-lg">
                <TrendingUp className="w-6 h-6 text-teal-600 dark:text-teal-400" />
              </div>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
              {platformStats.avgScreensPerOrg}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">Ø Screens / Org</p>
          </div>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          
          {/* Tier Distribution Pie Chart */}
          {allOrganizations.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Award className="w-5 h-5 text-purple-500" />
                Tier Distribution
              </h2>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={prepareTierDistribution()}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {prepareTierDistribution().map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Status Distribution Pie Chart */}
          {allOrganizations.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Activity className="w-5 h-5 text-green-500" />
                Subscription Status
              </h2>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={prepareStatusDistribution()}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {prepareStatusDistribution().map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}

        </div>

        {/* Top Performing Organizations */}
        {topOrgs.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-200 dark:border-gray-700 mb-8">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Award className="w-5 h-5 text-yellow-500" />
              Top 5 Organizations by Screen Count
            </h2>
            <div className="space-y-3">
              {topOrgs.map((org, index) => (
                <div key={org.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <div className="flex items-center gap-4">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${
                      index === 0 ? 'bg-yellow-500' :
                      index === 1 ? 'bg-gray-400' :
                      index === 2 ? 'bg-orange-600' :
                      'bg-gray-600'
                    }`}>
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 dark:text-white">{org.name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getTierBadgeColor(org.tier)}`}>
                          {org.tier}
                        </span>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(org.subscriptionStatus)}`}>
                          {org.subscriptionStatus}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{org.screenCount}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Screens</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Organization Comparison Chart */}
        {orgComparison.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-200 dark:border-gray-700 mb-8">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-500" />
              Top Organizations by MRR & Screens
            </h2>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={prepareOrgComparisonChart()}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="name" stroke="#9ca3af" angle={-45} textAnchor="end" height={120} />
                <YAxis stroke="#9ca3af" />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                  labelStyle={{ color: '#f3f4f6' }}
                />
                <Legend />
                <Bar dataKey="mrr" fill="#10b981" name="MRR (CHF)" />
                <Bar dataKey="screens" fill="#3b82f6" name="Screens" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* All Organizations Table */}
        {allOrganizations.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-200 dark:border-gray-700 mb-8">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-purple-500" />
              All Organizations
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Organization</th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Tier</th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Status</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Screens</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Media</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Playlists</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Users</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">MRR</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {allOrganizations.map((org) => (
                    <tr key={org.id} className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="py-3 px-4 text-sm text-gray-900 dark:text-white font-medium">
                        {org.name}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTierBadgeColor(org.tier)}`}>
                          {org.tier}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(org.subscriptionStatus)}`}>
                          {org.subscriptionStatus}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-900 dark:text-white text-right">
                        {org.screenCount}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400 text-right">
                        {org.mediaCount}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400 text-right">
                        {org.playlistCount}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400 text-right">
                        {org.userCount}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-900 dark:text-white text-right font-medium">
                        CHF {org.mrr}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400 text-right">
                        {new Date(org.createdAt).toLocaleDateString('de-DE')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Global Activity Timeline */}
        {globalActivity.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-200 dark:border-gray-700 mb-8">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Activity className="w-5 h-5 text-blue-500" />
              Global Activity Timeline
            </h2>
            <div className="space-y-3">
              {globalActivity.map((activity, index) => (
                <div key={activity.id} className="flex items-start gap-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                      <Users className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-medium text-gray-900 dark:text-white">
                        {activity.user_email}
                      </span>
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400">
                        {activity.action_type}
                      </span>
                      <span className="text-sm text-gray-500 dark:text-gray-500">
                        @ {activity.organization_name}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                      <span>{formatTimestamp(activity.timestamp)}</span>
                      {activity.resource_type && (
                        <span className="text-gray-500 dark:text-gray-500">
                          • {activity.resource_type}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default SuperAdminAnalytics;